import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { PromotionModel } from "@insite/client-framework/Types/ApiModels";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import CartLineQuantity, { CartLineQuantityStyles } from "@insite/content-library/Widgets/Cart/CartLineQuantity";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    cart: Cart;
    promotions?: PromotionModel[];
    editable: boolean;
    showInvalidPriceMessage?: boolean;
    extendedStyles?: CartLineCardCondensedStyles;
}

const mapStateToProps = (state: ApplicationState) => ({
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
    showPricing: state.context.showPricing,
    isCheckoutReviewAndSubmitPage: getCurrentPage(state).type === "CheckoutReviewAndSubmitPage",
});

type Props = OwnProps & HasCartLineContext & ReturnType<typeof mapStateToProps>;

export interface CartLineCardCondensedStyles {
    container?: GridContainerProps;
    productImageGridItem?: GridItemProps;
    productImage?: ProductImageStyles;
    orderLineInfoGridItem?: GridItemProps;
    orderLineInfoContainer?: GridContainerProps;
    infoLeftColumn?: GridItemProps;
    infoLeftColumnContainer?: GridContainerProps;
    productBrandAndDescriptionGridItem?: GridItemProps;
    productDescription?: ProductDescriptionStyles;
    productPriceAndQuantityGridItem?: GridItemProps;
    productPriceAndQuantityContainer?: GridContainerProps;
    priceGridItem?: GridItemProps;
    price?: ProductPriceStyles;
    priceHeadingAndText?: SmallHeadingAndTextStyles;
    quantityGridItem?: GridItemProps;
    quantity?: CartLineQuantityStyles;
    extendedUnitNetPriceGridItem?: GridItemProps;
    productNumberGridItem?: GridItemProps;
    extendedUnitNetPrice?: SmallHeadingAndTextStyles;
    productInfoPartNumberHeadingAndText?: SmallHeadingAndTextStyles;
}

export const cartLineCardCondensedStyles: CartLineCardCondensedStyles = {
    container: {
        gap: 20,
        css: css`
            border-bottom: 1px solid ${getColor("common.border")};
            padding: 1rem 0;
        `,
    },
    productImageGridItem: {
        width: 2,
    },
    orderLineInfoGridItem: {
        width: 10,
    },
    orderLineInfoContainer: {
        gap: 10,
    },
    infoLeftColumn: {
        width: [12, 12, 5, 5, 5],
    },
    infoLeftColumnContainer: {
        gap: 10,
    },
    productBrandAndDescriptionGridItem: {
        css: css`
            flex-direction: column;
            padding-bottom: 0px;
        `,
        width: 12,
    },
    productNumberGridItem: {
        width: [6, 6, 3, 3, 3],
        css: css`
            flex-direction: row;
            padding-top: 0px;
            padding-bottom: 20px;
        `,
    },
    priceGridItem: {
        width: [6, 6, 3, 3, 3],
        css: css`
            flex-direction: row;
            padding-bottom: 20px;
        `,
    },
    quantityGridItem: {
        width: [6, 6, 3, 3, 3],
        css: css`
            flex-direction: row;
            padding-bottom: 20px;
        `,
    },
    extendedUnitNetPriceGridItem: {
        width: [6, 6, 3, 3, 3],
        css: css`
            flex-direction: row;
            padding-bottom: 20px;
        `,
    },
    productPriceAndQuantityGridItem: {
        width: 12,
        css: css`
            margin-top: 5px;
            & ${GridOffset} {
                justify-content: space-between;
            }
        `,
    },
    productPriceAndQuantityContainer: {
        gap: 0,
    },
    extendedUnitNetPrice: {
        heading: {
            css: css`
                font-size: 14px;
                font-weight: bold;
                ${({ theme }) => {
                    return breakpointMediaQueries(theme, [
                        css`
                            text-align: left;
                        `,
                        css`
                            text-align: left;
                        `,
                        css`
                            text-align: center;
                        `,
                        css`
                            text-align: center;
                        `,
                        css`
                            text-align: center;
                        `,
                    ]);
                }}
            `,
        },
        text: {
            css: css`
                width: 100%;
                ${({ theme }) => {
                    return breakpointMediaQueries(theme, [
                        css`
                            text-align: left;
                        `,
                        css`
                            text-align: left;
                        `,
                        css`
                            text-align: center;
                        `,
                        css`
                            text-align: center;
                        `,
                        css`
                            text-align: center;
                        `,
                    ]);
                }}
            `,
        },
    },
    productInfoPartNumberHeadingAndText: {
        extendedStyles: {
            heading: {
                css: css`
                    font-size: 14px;
                    font-weight: bold;
                `,
            },
            text: {
                css: css`
                    text-align: left;
                    width: 100%;
                `,
            },
        },
    },
    price: {
        priceLabelText: {
            css: css`
                font-weight: bold;
                font-size: 14px;
                ${({ theme }) => {
                    return breakpointMediaQueries(theme, [
                        css`
                            text-align: left;
                        `,
                        css`
                            text-align: left;
                        `,
                        css`
                            text-align: center;
                        `,
                        css`
                            text-align: center;
                        `,
                        css`
                            text-align: center;
                        `,
                    ]);
                }}
            `,
        },
        priceWrapper: {
            css: css`
                ${({ theme }) => {
                    return breakpointMediaQueries(theme, [
                        css`
                            text-align: left;
                        `,
                        css`
                            text-align: left;
                        `,
                        css`
                            text-align: center;
                        `,
                        css`
                            text-align: center;
                        `,
                        css`
                            text-align: center;
                        `,
                    ]);
                }}
            `,
        },
        price: {
            priceText: { weight: "normal" },
        },
    },
    quantity: {
        readOnlyQuantityText: {
            extendedStyles: {
                heading: {
                    css: css`
                        font-size: 14px;
                        font-weight: bold;
                        ${({ theme }) => {
                            return breakpointMediaQueries(theme, [
                                css`
                                    text-align: left;
                                `,
                                css`
                                    text-align: left;
                                `,
                                css`
                                    text-align: center;
                                `,
                                css`
                                    text-align: center;
                                `,
                                css`
                                    text-align: center;
                                `,
                            ]);
                        }}
                    `,
                },
                text: {
                    css: css`
                        width: 100%;
                        ${({ theme }) => {
                            return breakpointMediaQueries(theme, [
                                css`
                                    text-align: left;
                                `,
                                css`
                                    text-align: left;
                                `,
                                css`
                                    text-align: center;
                                `,
                                css`
                                    text-align: center;
                                `,
                                css`
                                    text-align: center;
                                `,
                            ]);
                        }}
                    `,
                },
            },
        },
    },
};

const CartLineCardCondensed = ({
    cart,
    cartLine,
    editable,
    enableVat,
    vatPriceDisplay,
    showInvalidPriceMessage,
    showPricing,
    isCheckoutReviewAndSubmitPage,
    extendedStyles,
}: Props) => {
    const [styles] = React.useState(() => mergeToNew(cartLineCardCondensedStyles, extendedStyles));

    return (
        <GridContainer
            {...styles.container}
            data-test-selector={`cartline_condensed_${cartLine.productId}_${cartLine.unitOfMeasure}`}
        >
            <GridItem {...styles.productImageGridItem}>
                <ProductImage product={cartLine} extendedStyles={styles.productImage} />
            </GridItem>
            <GridItem {...styles.orderLineInfoGridItem}>
                <GridContainer {...styles.orderLineInfoContainer}>
                    <GridItem {...styles.productBrandAndDescriptionGridItem}>
                        <ProductDescription product={cartLine} extendedStyles={styles.productDescription} />
                    </GridItem>
                    <GridItem {...styles.productPriceAndQuantityGridItem}>
                        <GridContainer {...styles.productPriceAndQuantityContainer}>
                            <GridItem {...styles.productNumberGridItem}>
                                <SmallHeadingAndText
                                    {...styles.productInfoPartNumberHeadingAndText}
                                    heading={translate("Model No.")}
                                    text={`${cartLine.erpNumber}`}
                                />
                            </GridItem>
                            <GridItem {...styles.priceGridItem}>
                                <ProductPrice
                                    product={cartLine}
                                    currencySymbol={cart.currencySymbol}
                                    showSavings={true}
                                    showSavingsAmount={true}
                                    showSavingsPercent={true}
                                    showInvalidPriceMessage={true}
                                />
                            </GridItem>
                            <GridItem {...styles.quantityGridItem}>
                                <CartLineQuantity cart={cart} extendedStyles={styles.quantity} editable={editable} />
                            </GridItem>
                            <GridItem {...styles.extendedUnitNetPriceGridItem} data-test-selector="subtotal">
                                {cartLine.pricing && (showPricing || isCheckoutReviewAndSubmitPage) && (
                                    <SmallHeadingAndText
                                        heading={
                                            !enableVat
                                                ? translate("Subtotal")
                                                : vatPriceDisplay !== "DisplayWithoutVat"
                                                ? `${translate("Subtotal")} (${translate("Inc. VAT")})`
                                                : `${translate("Subtotal")} (${translate("Ex. VAT")})`
                                        }
                                        text={
                                            enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                                                ? cartLine.pricing.extendedUnitRegularPriceWithVatDisplay
                                                : cartLine.pricing.extendedUnitNetPriceDisplay
                                        }
                                        extendedStyles={styles.extendedUnitNetPrice}
                                    />
                                )}
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                </GridContainer>
            </GridItem>
        </GridContainer>
    );
};

export default connect(mapStateToProps)(withCartLine(CartLineCardCondensed));
